import { db } from "@/core";
import { useSalesStore } from "@/sites/sales/sales/store";
import {
	type ProjectRequest,
	type ProjectRequestTimelineFinalResultEntry,
	numbi,
} from "@msuite/katana";
import { Box, FormControl, Label, Text, VStack, useDocument, useUIContext } from "@msuite/picasso";
import { type FC, Fragment } from "react";

/** Props Interface */
interface TimelineFinalResultEntryProps {
	entry: ProjectRequestTimelineFinalResultEntry;
	projectRequestId: string;
}

export const TimelineFinalResultEntry: FC<TimelineFinalResultEntryProps> = ({
	entry,
	projectRequestId,
}) => {
	/** Store */
	const salesStore = useSalesStore();

	/** Hooks */
	const { data: projectRequest } = useDocument<ProjectRequest>(db, {
		path: `projectRequests/${projectRequestId}`,
		subscribe: true,
	});

	const selectedReason =
		salesStore.salesConfiguration?.projectLoseReasons
			.concat(salesStore.salesConfiguration.archivedProjectLoseReasons)
			.find((reason) => reason.id === entry.loseReason)?.name ?? "Sonstiges";

	/** Render */
	return (
		<Fragment>
			<Text>
				Projektanfrage wurde als <strong>{entry.result === "win" ? "gewonnen" : "verloren"}</strong>{" "}
				markiert.
			</Text>
			{entry.result === "lose" ? (
				<VStack spacing={4}>
					{entry.loseReason ? (
						<FormControl>
							<Label>Grund</Label>
							<Text>{selectedReason}</Text>
						</FormControl>
					) : null}
					{entry.loseReason === "misc" ? (
						<FormControl>
							<Label>Erläuterung (Sonstiges)</Label>
							<Text>{entry.loseReasonMisc}</Text>
						</FormControl>
					) : null}
					{entry.loseDescription ? (
						<FormControl>
							<Label>Beschreibung</Label>
							<Text>{entry.loseDescription}</Text>
						</FormControl>
					) : null}
				</VStack>
			) : (
				<FormControl>
					<Label>Auftragssumme</Label>
					<Text>{numbi(entry.contractSum).currency()}</Text>
					<DifferenceSpan
						offerSum={projectRequest?.offerSum}
						contractSum={entry.contractSum}
					/>
				</FormControl>
			)}
		</Fragment>
	);
};

function DifferenceSpan({
	offerSum = 0,
	contractSum = 0,
}: {
	offerSum: number | undefined;
	contractSum: number | undefined;
}) {
	/** Context */
	const { colors } = useUIContext();

	/** Calulcated */
	const difference = contractSum - offerSum;

	/** Guard */
	if (!difference) return null;

	const color = difference > 0 ? colors.emerald : colors.red;

	return (
		<Text
			fontSize="sm"
			fontWeight="medium"
		>
			Differenz zur Angebotssumme:{" "}
			<Box
				display="inline"
				color={color}
			>
				{numbi(difference).currency()}
			</Box>
		</Text>
	);
}
